// frontend/apps/widgets/src/widgets/TableReservation/view/TableReservationView.js
import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {useSearchParams} from 'react-router-dom';

// @mui
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Fade from '@mui/material/Fade';

// icons
import CloseIcon from '@mui/icons-material/Close';

// hooks
import {useReservationSteps} from '../hooks/useReservationSteps';
import {useReservationHandlers} from '../hooks/useReservationHandlers';
import {getErrorInfo, createStripeNotConfiguredError} from '../../common/hooks/useWidgetError';
import WidgetErrorBoundary from '../../common/components/WidgetErrorBoundary';
import {IframeResizeProvider, useIframeResize} from '../../common/contexts/IframeResizeContext';

// API
import {useExternalReservationConfig} from 'packages/common/hooks/api/widgets/table-reservations/Reservations';
import {useExternalCompanyDetails} from 'packages/common/hooks/api/widgets/company/Company';

// components
import {ReservationStepper} from '../components/ReservationStepper';
import {StepActions} from '../components/StepActions';
import {ConfigurationError} from '../components/ConfigurationError';

// utils
import {renderStepContent} from '../utils/stepContentRenderer';
import {isInIframe} from '../../../utils/iframeUtils';

// styled components
import {
  LoadingContainer,
  WidgetContainer,
  WidgetHeader,
  ContentContainer,
  FixedHeaderContainer,
  HeaderSpacerBox,
  ScrollableContentBox,
  FixedFooterContainer,
} from '../../common/components/styled/WidgetViewStyles';

export default function TableReservationView({
  mode = 'fixed',
  buttonText = 'Réserver une table',
  companyId: propCompanyId = null,
  apiKey: propApiKey = null,
}) {
  const [searchParams] = useSearchParams();
  const urlMode = searchParams.get('mode');

  // Parse params and props
  const embedded = isInIframe();
  const displayMode = (urlMode || mode).toLowerCase().trim();
  const companyId = propCompanyId || searchParams.get('companyId') || null;
  const apiKey = propApiKey || searchParams.get('apiKey') || null;
  const instanceId = searchParams.get('instanceId') || 'default-instance';
  const customColor = searchParams.get('customColor');

  return (
    <IframeResizeProvider
      mode={displayMode}
      instanceId={instanceId}
      minHeight={400}
      maxHeight={850}
      debounceDelay={200}
    >
      <WidgetErrorBoundary widgetType="reservations">
        <TableReservationContent
          mode={mode}
          displayMode={displayMode}
          embedded={embedded}
          companyId={companyId}
          apiKey={apiKey}
          instanceId={instanceId}
          customColor={customColor}
          buttonText={buttonText}
        />
      </WidgetErrorBoundary>
    </IframeResizeProvider>
  );
}

function TableReservationContent({
  mode,
  displayMode,
  embedded,
  companyId,
  apiKey,
  instanceId,
  customColor,
  buttonText,
}) {
  const {containerRef, updateHeight, forceResize} = useIframeResize();

  // Reservation steps management
  const {
    currentStep,
    formData,
    validationErrors,
    updateFormData,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    resetSteps,
    setValidationFunction,
    setErrors,
    STEPS,
  } = useReservationSteps();

  // Determine current state
  const isLastStep = currentStep === STEPS.DETAILS;
  const isConfirmationStep = currentStep === STEPS.CONFIRMATION;

  // API calls - Get data and errors from hooks
  const {
    config,
    isLoading: loadingConfig,
    isError: configError,
    error: configApiError,
  } = useExternalReservationConfig(companyId, apiKey);

  const {
    companyDetails,
    isLoading: isCompanyLoading,
    isError: isCompanyError,
    error: companyApiError,
  } = useExternalCompanyDetails(companyId, apiKey);

  // Determine which error to display (priority: company > config > stripe)
  const currentError = (() => {
    // First check API errors
    if (isCompanyError) return companyApiError;
    if (configError) return configApiError;

    // Then check Stripe configuration (only if company details loaded successfully)
    if (companyDetails && !companyDetails?.stripeAccountId) {
      return createStripeNotConfiguredError('reservations');
    }

    return null;
  })();

  // Set primary color with customColor or companyDetails primaryColor
  const primaryColor = customColor ||
                     companyDetails?.branding?.primaryColor ||
                     companyDetails?.primaryColor ||
                     null;

  // Step handlers
  const {
    isStepReady,
    setIsStepReady,
    submitLoading,
    handleComplete,
    handleCloseWidget,
    handleReset,
    provideValidationFunction,
    handleSetErrors,
    handleNextStep,
  } = useReservationHandlers({
    formData,
    config,
    currentStep,
    STEPS,
    embedded,
    companyId,
    apiKey,
    instanceId,
    goToStep,
    goToNextStep,
    resetSteps,
    setErrors,
    setValidationFunction,
  });

  // Update iframe height when content changes
  // Update iframe height when content changes
  useEffect(() => {
    if (displayMode === 'floating') {
      const timer = setTimeout(() => {
        updateHeight();
      }, 400); // Délai plus long
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayMode, currentStep]); // SEULEMENT currentStep et displayMode

  // Loading state
  if ((loadingConfig || isCompanyLoading) && !currentError) {
    return (
      <WidgetContainer embedded={embedded} ref={containerRef}>
        <LoadingContainer>
          <CircularProgress size={40} sx={{mb: 2}} color="primary" />
          <Typography variant="body2">Chargement...</Typography>
        </LoadingContainer>
      </WidgetContainer>
    );
  }

  // Unified Error state - Use getErrorInfo utility function
  if (currentError) {
    const errorInfo = getErrorInfo(currentError);
    return (
      <WidgetContainer embedded={embedded} ref={containerRef}>
        <WidgetHeader variant="error">
          <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>
            {errorInfo.title}
          </Typography>
          {displayMode === 'floating' && (
            <IconButton size="small" onClick={handleCloseWidget} sx={{color: 'inherit'}}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </WidgetHeader>
        <ContentContainer>
          <ConfigurationError
            title={errorInfo.title}
            message={errorInfo.message}
            details={errorInfo.details}
            onClose={handleCloseWidget}
            showCloseButton={embedded && displayMode === 'floating'}
          />
        </ContentContainer>
      </WidgetContainer>
    );
  }

  // Success state - render the reservation flow
  return (
    <Box sx={{
      ...(!embedded && {
        paddingTop: 4,
      }),
    }}>
      <WidgetContainer
        embedded={embedded}
        displayMode={displayMode}
        ref={containerRef}
        maxWidth="512px"
        maxHeight="100%"
      >
        {/* Header - fixed */}
        <FixedHeaderContainer embedded={embedded} displayMode={displayMode}>
          <WidgetHeader primaryColor={primaryColor}>
            <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>
              {isConfirmationStep ? 'Réservation confirmée' : 'Réserver une table'}
            </Typography>
            {embedded && displayMode === 'floating' && (
              <IconButton size="small" onClick={handleCloseWidget} sx={{color: 'inherit'}}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </WidgetHeader>
          {!isConfirmationStep && <ReservationStepper activeStep={currentStep} primaryColor={primaryColor} />}
        </FixedHeaderContainer>

        {/* Spacer for header */}
        <HeaderSpacerBox hasSteps={!isConfirmationStep} embedded={embedded} />

        {/* Scrollable content */}
        <ScrollableContentBox hasFooter={!isConfirmationStep} embedded={embedded}>
          <Fade in timeout={300} onEntered={forceResize}>
            <Box>
              {renderStepContent({
                currentStep,
                STEPS,
                formData,
                updateFormData,
                config,
                validationErrors,
                provideValidationFunction,
                handleSetErrors,
                setIsStepReady,
                handleComplete,
                handleReset,
                handleCloseWidget,
                companyId,
                apiKey,
                displayMode,
                embedded,
              })}
            </Box>
          </Fade>
        </ScrollableContentBox>

        {/* Footer - fixed */}
        {!isConfirmationStep && (
          <FixedFooterContainer embedded={embedded} displayMode={displayMode}>
            <StepActions
              showBack={currentStep > STEPS.SERVICE_GUESTS}
              onBack={goToPreviousStep}
              onNext={handleNextStep}
              isLastStep={isLastStep}
              isNextLoading={isLastStep && submitLoading}
              nextButtonText={isLastStep ? 'Confirmer' : 'Suivant'}
              disableNext={!isStepReady}
            />
          </FixedFooterContainer>
        )}
      </WidgetContainer>
    </Box>
  );
}

TableReservationContent.propTypes = {
  mode: PropTypes.string.isRequired,
  displayMode: PropTypes.string.isRequired,
  embedded: PropTypes.bool.isRequired,
  companyId: PropTypes.string,
  apiKey: PropTypes.string,
  instanceId: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
};

TableReservationView.propTypes = {
  mode: PropTypes.oneOf(['fixed', 'floating']),
  buttonText: PropTypes.string,
  companyId: PropTypes.string,
  apiKey: PropTypes.string,
};
